import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Button,
  Container,
  Group,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { WorkoutDetailView, WorkoutTimerButton } from 'components/Workout';
import useKioskEvent from 'hooks/event/useKioskEvent';
import useClassWorkoutDetails from 'hooks/workout/useClassWorkoutDetails';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getFormattedCoachesNames } from 'utils/event.helpers';
import { formatDatesForEvent } from 'utils/formatters';

const KioskWorkout = (): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, true);

  const eventType = searchParams.get('eventType') as 'class' | 'appointment';

  const iskioskMode = localStorage.getItem('isKiosk') === 'true';

  const { data: selectedEventData, isLoading: isEventLoading } = useKioskEvent({
    eventId: eventId || '',
    eventType: eventType,
    refetchInterval: 60000,
  });

  const selectedEvent = useMemo(() => selectedEventData?.data, [selectedEventData]);

  const {
    data: workoutData,
    isLoading: isWorkoutLoading,
    isError: isWorkoutError,
  } = useClassWorkoutDetails({
    kiloClassId: eventType === 'class' && eventId ? eventId : undefined,
  });

  const structuredWorkout = workoutData?.data ?? null;

  const eventDateAndCoachesLine = useMemo(() => {
    return `${formatDatesForEvent(selectedEvent)} / ${getFormattedCoachesNames(
      selectedEvent,
      isDesktop ? 2 : 1,
    )}`;
  }, [selectedEvent, isDesktop]);

  const goBack = useCallback((): void => {
    navigate(`/kiosk/${eventId}?eventType=${eventType}`);
  }, [navigate, eventId, eventType]);

  const goToWhiteboard = useCallback((): void => {
    navigate('/workouts/whiteboard', {
      state: {
        kiloClassId: eventType === 'class' && eventId ? eventId : undefined,
        kiloClassTypeId: structuredWorkout?.kiloClassType?.kiloClassTypeId,
        date: structuredWorkout?.date,
      },
    });
  }, [navigate, eventId, eventType, structuredWorkout]);

  const detailPath = `/kiosk/${eventId}?eventType=${eventType}`;

  const isLoading = isEventLoading || isWorkoutLoading;

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (!structuredWorkout) {
    return (
      <Container w='100%'>
        <Stack spacing='lg' py='xl'>
          <Group
            bg='#15B8FF'
            w={48}
            h={48}
            style={{
              borderRadius: '50%',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={goBack}
          >
            <IconChevronLeft color='white' />
          </Group>

          <Stack spacing={4}>
            <Text size='sm' color='dimmed' weight={400}>
              {selectedEvent?.eventName ?? ''} / {eventDateAndCoachesLine}
            </Text>
            <Title order={1} size={isDesktop ? 32 : 24} weight={600}>
              No workout assigned
            </Title>
          </Stack>
        </Stack>
      </Container>
    );
  }

  if (isWorkoutError) {
    return <Navigate to={detailPath} replace />;
  }

  return (
    <ScrollArea bg='white' h='100vh'>
      <Box sx={{ position: 'fixed', top: 24, right: 24, zIndex: 300 }}>
        <WorkoutTimerButton />
      </Box>

      <Container w='100%'>
        <Stack spacing='lg' py='xl'>
          <Group
            bg='#15B8FF'
            w={48}
            h={48}
            style={{
              borderRadius: '50%',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={goBack}
          >
            <IconChevronLeft color='white' />
          </Group>

          <Stack spacing={4}>
            <Text size='sm' color='dimmed' weight={400}>
              {selectedEvent?.eventName ?? ''} / {eventDateAndCoachesLine}
            </Text>
            <Title order={1} size={isDesktop ? 32 : 24} weight={600}>
              {structuredWorkout.name ?? selectedEvent?.eventName ?? 'Workout'}
            </Title>
          </Stack>

          {!iskioskMode && (
            <Group>
              <Button
                variant='subtle'
                color='blue'
                rightIcon={<IconChevronRight size={18} />}
                onClick={goToWhiteboard}
                sx={{
                  textDecoration: 'underline',
                  padding: 0,
                  height: 'auto',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                View Whiteboard
              </Button>
            </Group>
          )}

          <WorkoutDetailView workout={structuredWorkout} isCoachView={!iskioskMode} />
        </Stack>
      </Container>
    </ScrollArea>
  );
};

export default KioskWorkout;
